import { BehaviorSubject } from "rxjs";

import { createBrowserHistory } from "history";

const userJson = localStorage.getItem("currentUser");
const user = userJson !== null ? JSON.parse(userJson) : null;

const currentUserSubject = new BehaviorSubject(user);

export interface AuthenticatedUser {
  access_token: string;
  id_token: string;
  refresh_token: string;
  expires: number;
  groups: string[];
}

export const authenticationService = {
  login,
  logout,
  setPermanentPassword,
  forgotPassword,
  setNewPassword,
  resetTempCode,
  currentUser: currentUserSubject.asObservable(),
  get currentUserValue() {
    return currentUserSubject.value;
  },
};

export const history = createBrowserHistory();

const authUrl = process.env.REACT_APP_API_URL;

function login(username: string, password: string) {
  const requestOptions = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  };

  return fetch(`${authUrl}/signin`, requestOptions)
    .then(handleResponse)
    .then((res) => {
      // Only set the currentUser if response was authenticated
      if (res.key === "authenticated") {
        localStorage.setItem("currentUser", JSON.stringify(res));
        currentUserSubject.next(res);
      }

      return res;
    });
}

function logout() {
  // remove user from local storage to log user out
  localStorage.removeItem("currentUser");
  currentUserSubject.next(null);
}

/**
 * Sets a permanent password for a user since a temp password is first assigned
 */
function setPermanentPassword(
  session: string,
  userId: string,
  password1: string,
  password2: string
) {
  const requestOptions = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session, password1, password2, username: userId }),
  };

  return fetch(`${authUrl}/set-password`, requestOptions)
    .then(handleResponse)
    .then((res) => {
      // Only set the currentUser if response was authenticated
      if (res.key === "authenticated") {
        localStorage.setItem("currentUser", JSON.stringify(res));
        currentUserSubject.next(res);
      }

      return res;
    });
}

function forgotPassword(email: string) {
  const requestOptions = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  };

  return fetch(`${authUrl}/forgot-password`, requestOptions)
    .then(handleResponse)
    .then((res) => {
      return res;
    });
}

function setNewPassword(
  code: string,
  password1: string,
  password2: string,
  email: string
) {
  const requestOptions = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code, password1, password2, email }),
  };

  return fetch(`${authUrl}/set-new-password`, requestOptions)
    .then(handleResponse)
    .then((res) => {
      return res;
    });
}

function resetTempCode(email: string) {
  const token = localStorage["currentUser"];
  const requestOptions = {
    method: "POST",
    // headers: { "Content-Type": "application/json" },
    headers: {
      Authorization: `Bearer ${JSON.parse(token).id_token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ username: email }),
  };

  return fetch(`${authUrl}/reset-temp-code`, requestOptions)
    .then(handleResponse)
    .then((res) => {
      return res;
    });
}

export function authHeader() {
  // return authorization header with jwt token
  const token = localStorage.getItem("currentUser");
  if (token) {
    return { Authorization: `Bearer ${JSON.parse(token).id_token}` };
  }
  return {};
}

export function getUserObject() {
  const token = JSON.parse(localStorage.getItem("currentUser") ?? "[]") ?? "";
  if (token) {
    return token as AuthenticatedUser;
  } else {
    throw Error("User object is not set");
  }
}

export function getGroup() {
  const user = getUserObject();
  const group = ((user as AuthenticatedUser).groups || []).filter(
    (g) => g !== "admin"
  );
  if (group.length > 0) {
    return group[0];
  } else {
    throw Error("Could not get group for user");
  }
}

export function handleResponse(response: Response) {
  return response.text().then((text) => {
    const data = text && JSON.parse(text);
    if (!response.ok) {
      if ([401, 403].indexOf(response.status) !== -1) {
        // auto logout if 401 Unauthorized or 403 Forbidden response returned from api
        authenticationService.logout();
        window.location.reload(true);
      }

      const error = (data && data.detail) || response.statusText;
      return Promise.reject(error);
    }

    return data;
  });
}
